import { useQuery } from 'react-query';
import api from 'api';
import type {
  OTFormulaCard,
  OTFormulaCardTypeMapping,
  OTEntry,
} from 'api/gql-utils/overtime/types';
import { OT_FORMULA_CARD_TYPE_MAPPINGS_QUERY_KEY } from 'components/Settings/AttendanceAndLeavesSettings/PayRulesTab/components/OvertimeSection/hooks/useOtFormulaCardTypeMappings';
import queryKeys from '../../queries';
import loggedInUserSelectors from 'reducers/loggedInUser/selectors';
import useReduxSelector from 'utils/useReduxSelector';

type UseOvertimeQueriesResult = {
  formulaCards: OTFormulaCard[];
  typeMappings: OTFormulaCardTypeMapping[];
  entries: OTEntry[];
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
};

export function useOvertimeQueries({
  payrollMonth,
  userId,
  enabled,
  baseDays,
  onSuccess,
}: {
  payrollMonth: string;
  userId: string;
  enabled: boolean;
  baseDays?: string;
  onSuccess?: (data: OTEntry[]) => void;
}): UseOvertimeQueriesResult {
  const currentOrganization = useReduxSelector(loggedInUserSelectors.currentOrganization);
  const isUbitechOrg = !!currentOrganization?.isUbitechOrg;

  const formulaCardsQuery = useQuery({
    queryKey: [queryKeys.OVERTIME_FORMULA_CARDS_KEY],
    queryFn: () => api.overtime.queries.listFormulaCards({ status: 'active' }),
    enabled,
    select: (response) => response.overtimeListFormulaCards.data.otFormulaCards,
  });

  const typeMappingsQuery = useQuery({
    queryKey: [OT_FORMULA_CARD_TYPE_MAPPINGS_QUERY_KEY],
    queryFn: () => api.overtime.queries.listFormulaCardTypeMappings(),
    enabled: enabled && isUbitechOrg,
    select: (response) => response.overtimeListFormulaCardTypeMappings.data.typeMappings || [],
  });

  const entriesQuery = useQuery({
    queryKey: [queryKeys.OVERTIME_LIST_ENTRIES_KEY, payrollMonth, userId, baseDays],
    queryFn: () => api.overtime.queries.listEntries({ payrollMonth, userId, baseDays }),
    enabled: enabled,
    select: (response) => response.overtimeListEntries.data.entries,
    onSuccess: (entries) => {
      onSuccess && onSuccess(entries);
    },
  });

  return {
    formulaCards: formulaCardsQuery.data ?? [],
    typeMappings: typeMappingsQuery.data ?? [],
    entries: entriesQuery.data ?? [],
    isLoading:
      !enabled ||
      formulaCardsQuery.isLoading ||
      typeMappingsQuery.isLoading ||
      entriesQuery.isLoading,
    isError: formulaCardsQuery.isError || typeMappingsQuery.isError || entriesQuery.isError,
    refetch: () => {
      formulaCardsQuery.refetch();
      if (isUbitechOrg) {
        typeMappingsQuery.refetch();
      }
      entriesQuery.refetch();
    },
  };
}
